import {APP_BASE_HREF} from '@angular/common';
import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Cancelable} from 'lodash';
import debounce from 'lodash-es/debounce';
import {BehaviorSubject, combineLatest, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, filter, map, pluck, switchMap, tap} from 'rxjs/operators';
import {cache, closest, KEY_ENTER, KEY_ESC} from '../../../utils';
import {AnalyticsService} from '../../shared/analytics.service';
import {HttpError} from '../../shared/api.service';
import {AuthService} from '../../shared/auth.service';
import {IdentifyModalComponent} from '../../shared/identify-modal/identify-modal.component';
import {NavigationService} from '../../shared/navigation.service';
import {ClientApiService} from '../client-api.service';
import {TalentCardPipe, TalentCardSearchOptions} from '../talent-card.pipe';

@Component({
  selector: 'pools-page',
  templateUrl: './pools-page.component.html',
  styleUrls: ['../styles/paginator.scss', '../styles/search-sort.scss', './pools-page.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class PoolsPageComponent implements OnInit, OnDestroy {

  talentSortOptions = new Map<string, [string, {}]>([
    ['date-desc', ['pools.talent-sort.date-desc', {}]],
    ['date-asc', ['pools.talent-sort.date-asc', {}]],
    ['rate-desc', ['pools.talent-sort.rate-desc', {}]],
    ['rate-asc', ['pools.talent-sort.rate-asc', {}]],
    ['availability', ['pools.talent-sort.availability', {}]],
    ['rating', ['pools.talent-sort.rating', {}]]
  ]);
  poolsSortOptions = new Map<string, [string, {}]>([
    ['updated-desc', ['pools.pools-sort.updated-desc', {}]],
    ['updated-asc', ['pools.pools-sort.updated-asc', {}]],
    ['created-desc', ['pools.pools-sort.created-desc', {}]],
    ['created-asc', ['pools.pools-sort.created-asc', {}]]
  ]);
  countOptions = new Map<number, [string, {}]>([
    [9, ['pagination.count-option', {number: 9}]],
    [30, ['pagination.count-option', {number: 30}]],
    [90, ['pagination.count-option', {number: 90}]]
  ]);

  private defaultSearch: TalentCardSearchOptions = {
    sort: 'date-desc',
    filter: 'all',
    query: null
  };

  reload$ = new Subject<void>();
  search$ = new BehaviorSubject<TalentCardSearchOptions>(this.defaultSearch);
  loading$ = new BehaviorSubject(true);
  currentPage$ = new BehaviorSubject(1);
  pageSize$ = new BehaviorSubject(30);
  hasSearch$ = new BehaviorSubject(false);
  favorites$ = new BehaviorSubject<string[]>([]);
  sort$ = new BehaviorSubject<string>('updated-desc');

  view$ = this.route.params.pipe(pluck('view'));
  user$ = this.Auth.user$;

  positions$: Observable<Order[]>;
  allSubmittals$: Observable<Submittal[]>;
  submittals$: Observable<Submittal[]>;
  position$: Observable<Order>;
  lowVal$: Observable<number>;
  highVal$: Observable<number>;
  total$: Observable<number>;
  staff$: Observable<StaffInfo[]>;

  searchText: string | null = null;

  @ViewChild('identifyModal') identifyModal: IdentifyModalComponent;

  filterOptions$ = this.view$.pipe(map(view =>
    new Map<string, [string, {}]>(view === 'favorites' ? [
      ['all', ['pools.filter.all', {}]],
      ['recently-added', ['pools.filter.recently-added', {}]],
      ['previously-hired', ['pools.filter.previously-hired', {}]],
      ['currently-working', ['pools.filter.currently-working', {}]]
    ] : [
      ['all', ['pools.filter.all', {}]],
      ['favorited', ['pools.filter.favorited', {}]],
      ['recently-added', ['pools.filter.recently-added', {}]],
      ['previously-hired', ['pools.filter.previously-hired', {}]],
      ['currently-working', ['pools.filter.currently-working', {}]]
    ])));

  private ordersSub: Subscription;
  private favoritesSub: Subscription;
  private transitionSub: Function;
  private submitSearchAnalytics: (() => void) & Cancelable;
  private identificationCallback: Function | null = null;

  constructor(private Router: Router,
              private route: ActivatedRoute,
              private Navigation: NavigationService,
              private Api: ClientApiService,
              private Search: TalentCardPipe,
              private Analytics: AnalyticsService,
              private Auth: AuthService,
              @Inject(APP_BASE_HREF) private basePath: string) {
  }

  ngOnInit() {
    const data = this.reload$.pipe(
      tap(() => this.loading$.next(true)),
      switchMap(() => {
        if (this.Auth.isFullyAuthenticated()
          && !this.Auth.user$.getValue().admin) {
          return this.Api.getPools();
        } else {
          // TODO avoid using route snapshot
          return this.Api.getPool(this.route.snapshot.paramMap.get('view'));
        }
      }),
      tap(() => this.loading$.next(false)),
      catchError((err: HttpError) => {
        console.error('Failed to load positions.', err);
        this.Navigation.handleError(err);
        return of(null);
      }),
      cache()
    );
    this.positions$ = data.pipe(map(v => v.orders));
    this.allSubmittals$ = data.pipe(map(v => v.submittals));
    this.position$ = combineLatest(this.positions$, this.view$,
      (positions, view) => {
        const position = positions.find(p => p.orderId === view);
        // More elegant not-found handling?
        if (view !== 'talent' && view !== 'all' && view !== 'favorites' && !position) {
          this.Router.navigate(['../all'], {relativeTo: this.route});
        }
        return position;
      })
      .pipe(
        switchMap(position => (position && !this.Auth.masquerading$.getValue())
          ? this.Api.reviewPool(position.orderId)
            .pipe(
              map(() => {
                position.reviewed = true;
                position.numUnreviewedCandidates = 0;
                return position;
              }),
              catchError((err: HttpError) => {
                console.error('Failed to save event.', err);
                return of(position);
              })
            )
          : of(position)),
        tap(() => {
          this.search$.next(this.defaultSearch);
          this.searchText = null;
          this.searchTextChanged();
        }),
        cache()
      );
    this.submittals$ = combineLatest(this.allSubmittals$, this.search$, this.position$, this.view$,
      (submittals, search, position, view) => {
        if (view === 'favorites') {
          submittals = this.Search.transform(submittals, {filter: 'favorited', favorites: this.favorites$.getValue()});
        }
        return this.Search.transform(submittals, search, position && position.orderId);
      })
      .pipe(
        tap(() => this.currentPage$.next(1)),
        cache()
      );
    this.favoritesSub = this.Api.getFavorites()
      .subscribe(favorites => this.updateFavorites(favorites));

    // Trigger initial HTTP request
    this.ordersSub = data.subscribe();
    this.reload$.next();

    this.lowVal$ = combineLatest(this.currentPage$, this.pageSize$,
      (currentPage, pageSize) => ((currentPage - 1) * pageSize) + 1);
    this.highVal$ = combineLatest(this.currentPage$, this.pageSize$, this.submittals$,
      (currentPage, pageSize, submittals) => Math.min(submittals.length, currentPage * pageSize));
    this.total$ = this.submittals$.pipe(map(submittals => submittals.length));

    this.submitSearchAnalytics = debounce(() => {
      if (this.searchText) {
        this.Analytics.trackEvent('Talent Pools: Search', {
          category: 'Action',
          searchText: this.searchText
        });
      }
    }, 2000);

    this.staff$ = this.Auth.user$.pipe(filter(v => !!v), pluck('staff'));
  }

  ngOnDestroy() {
    if (this.ordersSub) this.ordersSub.unsubscribe();
    if (this.favoritesSub) this.favoritesSub.unsubscribe();
    if (this.transitionSub) this.transitionSub();
    if (this.submitSearchAnalytics) this.submitSearchAnalytics.flush();
  }

  reload() {
    this.reload$.next();

    if (this.favoritesSub) this.favoritesSub.unsubscribe();
    this.favoritesSub = this.Api.getFavorites()
      .subscribe(favorites => this.updateFavorites(favorites));
  }

  setSort(value: string): void {
    this.updateSearch({sort: value});
    this.Analytics.trackEvent(`Talent Pools: Sort set to ${value}`);
  }

  setFilter(value: string): void {
    this.updateSearch({filter: value});
    this.Analytics.trackEvent(`Talent Pools: Filter set to ${value}`);
  }

  setCount(count: number): void {
    this.pageSize$.next(count);
    this.currentPage$.next(1);
  }

  searchTextChanged(): void {
    this.updateSearch({query: this.searchText});
    this.hasSearch$.next(!!this.searchText);
    this.submitSearchAnalytics();
  }

  clearSearch(): void {
    this.searchText = null;
    this.searchTextChanged();
  }

  searchKeydown(event: KeyboardEvent): void {
    if (event.keyCode === KEY_ESC) {
      this.clearSearch();
      event.preventDefault();
    } else if (event.keyCode === KEY_ENTER) {
      (event.target as HTMLElement).blur();
      event.preventDefault();
    }
  }

  onFavorite(data: { id: string, favorite: boolean }) {
    const favorites = new Set(this.search.favorites);
    if (data.favorite) {
      favorites.add(data.id);
    } else {
      favorites.delete(data.id);
    }
    this.updateFavorites(Array.from(favorites));
  }

  showIdentification(event: Event | null, data?: { event?: Event, callback?: Function }) {
    if (!this.Auth.isFullyAuthenticated()) {
      if (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      event = event || (data && data.event);
      let callback = data && data.callback;
      if (event && !callback) {
        const elem = closest(event.target as HTMLElement, el => el.tagName.toLowerCase() === 'a');
        if (elem && elem.hasAttribute('href')) {
          const href = `/${elem.getAttribute('href').substring(this.basePath.length)}`;
          callback = () => this.Router.navigateByUrl(href);
        }
      }
      this.identificationCallback = callback;
      this.identifyModal.show();
    }
  }

  identifySuccess() {
    if (this.identificationCallback) {
      const ret = this.identificationCallback();
      if (ret && 'then' in ret) {
        ret.then(() => this.reload());
        return;
      }
    }
    this.reload();
  }

  get search() {
    return this.search$.getValue();
  }

  private updateSearch(data: Partial<TalentCardSearchOptions>) {
    this.search$.next({...this.search, ...data});
  }

  private updateFavorites(favorites: string[]) {
    this.favorites$.next(favorites);
    this.defaultSearch.favorites = favorites;
    this.updateSearch({favorites});
  }
}
